import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useCallback,
  useState,
} from "react";
import { fetchSlotStateBatch } from "../../../../api/clinicalTrial";
import { HandleApiError } from "../../../Notifications/APIErrorHandler";

const AttachmentSlotPollingContext = createContext(null);

export function AttachmentSlotPollingProvider({ children, intervalMs = 4000 }) {
  const listenersRef = useRef(new Map()); // key -> Set(callback)
  const lastSinceRef = useRef(new Map()); // key -> ISO string (UpdatedAt)
  const activeRef = useRef(new Map()); // key -> { key, typeId, categoryId, entityId }

  const [availableUsers, setAvailableUsers] = useState([]);
  const [currentUser, setCurrentUser] = useState(null);

  const subscribe = useCallback((slot) => {
    const key = slot.key;

    if (!listenersRef.current.has(key)) {
      listenersRef.current.set(key, new Set());
    }

    activeRef.current.set(key, slot);

    return (cb) => {
      const set = listenersRef.current.get(key);
      if (set) {
        set.add(cb);
      }

      return () => {
        const current = listenersRef.current.get(key);
        if (current) {
          current.delete(cb);
          if (current.size === 0) {
            listenersRef.current.delete(key);
            activeRef.current.delete(key);
            lastSinceRef.current.delete(key);
          }
        }
      };
    };
  }, []);

  const setUsersFromProtocolPractitioners = useCallback(
    (protocolPractitioners) => {
      const list = (protocolPractitioners ?? [])
        .map((pp) => {
          const id = pp?.practitionerUserId
            ? String(pp.practitionerUserId)
            : null;

          const name =
            pp?.practitioner?.fullName ||
            pp?.practitioner?.FullName ||
            pp?.practitioner?.name ||
            pp?.practitioner?.Name ||
            "Usuario";

          return { id, name };
        })
        .filter((u) => !!u.id);

      const unique = Array.from(new Map(list.map((u) => [u.id, u])).values());

      setAvailableUsers(unique);
    },
    []
  );

  const setCurrentUserFromAuth = useCallback((authUser) => {
    if (!authUser) {
      setCurrentUser(null);
      return;
    }

    const id =
      authUser.id ||
      authUser.userId ||
      authUser.sub ||
      authUser.UserId ||
      authUser.Id;

    const name =
      authUser.fullName ||
      authUser.FullName ||
      authUser.name ||
      authUser.Name ||
      authUser.email ||
      authUser.Email ||
      "Usuario";

    if (!id) {
      setCurrentUser(null);
      return;
    }

    setCurrentUser({ id: String(id), name });
  }, []);

  useEffect(() => {
    let timerId = null;
    let stopped = false;

    const tick = async () => {
      if (stopped) return;

      const activeSlots = Array.from(activeRef.current.values());
      if (activeSlots.length === 0) return;

      const batchItems = activeSlots.map((s) => ({
        typeId: s.typeId,
        categoryId: s.categoryId,
        entityId: s.entityId,
        sinceUtc: lastSinceRef.current.get(s.key) || null,
      }));

      try {
        const res = await fetchSlotStateBatch(batchItems);
        const items = res?.items || [];

        for (const it of items) {
          if (it.notModified) continue;
          if (!it.state) continue;

          const updatedAt = it.state.updatedAt;
          if (updatedAt) {
            lastSinceRef.current.set(it.key, updatedAt);
          }

          const cbs = listenersRef.current.get(it.key);
          if (!cbs || cbs.size === 0) continue;

          for (const cb of cbs) {
            cb(it.state);
          }
        }
      } catch (error) {
        HandleApiError(error);
      }
    };

    timerId = setInterval(tick, intervalMs);
    tick();

    return () => {
      stopped = true;
      if (timerId) clearInterval(timerId);
    };
  }, [intervalMs]);

  const api = useMemo(() => {
    const currentUserId = currentUser?.id || null;
    const currentUserName = currentUser?.name || null;

    const users = (availableUsers || []).map((u) => ({
      ...u,
      currentUser: !!currentUserId && String(u.id) === String(currentUserId),
    }));

    if (
      currentUserId &&
      !users.some((u) => String(u.id) === String(currentUserId))
    ) {
      users.unshift({
        id: currentUserId,
        name: currentUserName || "Usuario",
        currentUser: true,
      });
    }

    return {
      subscribe,
      availableUsers: users,
      currentUserId,
      currentUserName,
      setUsersFromProtocolPractitioners,
      setCurrentUserFromAuth,
    };
  }, [
    subscribe,
    availableUsers,
    currentUser,
    setUsersFromProtocolPractitioners,
    setCurrentUserFromAuth,
  ]);

  return (
    <AttachmentSlotPollingContext.Provider value={api}>
      {children}
    </AttachmentSlotPollingContext.Provider>
  );
}

export function useAttachmentSlotPolling() {
  return useContext(AttachmentSlotPollingContext); // puede ser null
}
